import { useState, useEffect, useRef, useMemo, useImperativeHandle, forwardRef, ComponentType, ReactNode, ForwardedRef, CSSProperties } from 'react';
import { Upload, FileText, Image, File as FileIcon, Archive, ArchiveRestore } from 'lucide-react';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import FilePreviewModalRaw from '@/components/ui/FilePreviewModal';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/errorMessageTranslator';
import { ReconnectingError, isReconnectingError } from '@/services/errors';
import { getSessionUser } from '@/lib/auth/sessionUser';
import ConfirmDeleteModalRaw, { FileDeleteContent as FileDeleteContentRaw } from '@/components/ui/ConfirmDeleteModal';
import FileEditModalRaw from '@/components/ui/FileEditModal';
import PenAnimatedIcon from '@/components/ui/PenAnimatedIcon';
import ConfirmActionModal from '@/components/ui/ConfirmActionModal';
import { resumableUpload } from '@/lib/resumableUpload';

// Raw-alias: sibling modals still have divergent Props shape from what we forward.
// Single-step boundary casts — follow-up: type these siblings directly.
const FilePreviewModal = FilePreviewModalRaw as unknown as ComponentType<Record<string, unknown>>;
const ConfirmDeleteModal = ConfirmDeleteModalRaw as unknown as ComponentType<Record<string, unknown>>;
const FileEditModal = FileEditModalRaw as unknown as ComponentType<Record<string, unknown>>;
const FileDeleteContent = FileDeleteContentRaw as unknown as ComponentType<Record<string, unknown>>;

// ============================================
// Companion types (file-local)
// ============================================

type GenericFileRow = Record<string, unknown> & { id: string };

interface EntityFieldConfig {
  key: string;
  label?: string;
  type?: string;
  placeholder?: string;
  optionsTable?: string;
  optionsValueField?: string;
  optionsLabelField?: string;
  options?: Array<{ value: string | number; label: string }>;
  [key: string]: unknown;
}

interface EntityConfigEntry {
  tableName: string;
  junctionTable?: string;
  bucketName: string;
  storagePath?: string;
  mode?: 'catalog' | 'junction' | 'foreignKey' | 'polymorphic' | string;
  catalogMode?: boolean;
  catalogFilter?: Record<string, unknown> & { tipo?: string };
  entidadeValue?: string;
  hasObraId?: boolean;
  storagePathField: string;
  originalNameField?: string | null;
  descriptionField?: string | null;
  mimeTypeField?: string | null;
  sizeField?: string | null;
  foreignKey?: string;
  additionalForeignKey?: string;
  selectOverride?: string;
  softDelete?: boolean;
  maxFileSize?: number;
  customFields?: EntityFieldConfig[];
  extraInsertData?: (pessoaData: { id: string; tenant_id: string; filial_id?: string | null }) => Record<string, unknown>;
  listBadge?: (anexo: GenericFileRow) => { text: string; bg: string; color: string } | null;
  listPrimary?: (anexo: GenericFileRow) => string | null;
  listSecondary?: (anexo: GenericFileRow) => string | null;
  [key: string]: unknown;
}

interface ActiveUpload {
  abort: () => void;
}

export interface GenericFileHandle {
  openUpload: () => void;
  reload: () => void;
}

export interface GenericFileProps {
  entityId?: string | null;
  entityType?: string;
  additionalEntityId?: string | null;
  readOnly?: boolean;
  onChange?: (files: GenericFileRow[]) => void;
  refreshKey?: number;
  recurso?: string;
  compact?: boolean;
  searchTerm?: string;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  onDataChange?: (data: GenericFileRow[]) => void;
  gridColumns?: number;
  layout?: 'grid' | 'list';
  deleteModalTitle?: string;
  /** Catálogo de file (proposta_imagem): revela imagens arquivadas na gestão. */
  showArchived?: boolean;
}

interface CategoriaRow {
  id: string;
  nome: string;
  ordem: number | null;
}

interface FileEditPayload {
  descricao?: string | null;
  newFile?: File | null;
  categoriaId?: string | null;
  customData?: Record<string, unknown> | null;
}

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
const RESUMABLE_THRESHOLD = 50 * 1024 * 1024; // Above 50MB uses resumable upload
const ALLOWED_TYPES = [
  // Imagens
  'image/jpeg',
  'image/png',
  'image/gif',
  // PDF
  'application/pdf',
  // XML
  'application/xml',
  'text/xml',
  // Documentos Office - Word
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  // Documentos Office - Excel
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  // Documentos Office - PowerPoint
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  // CSV
  'text/csv',
  'application/csv',
  // Google Sheets
  'application/vnd.google-apps.spreadsheet',
  // DWG (AutoCAD) - vários MIME types possíveis
  'application/acad',
  'application/x-acad',
  'application/dwg',
  'image/vnd.dwg',
  'image/x-dwg',
  'application/x-dwg'
];
const ALLOWED_EXTENSIONS = [
  '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.xml',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.csv', '.gsheet', '.dwg'
];

// Helper para obter mime type do caminho do arquivo (fora do componente)
const getMimeTypeFromPath = (path: string | null | undefined): string | null => {
  if (!path) return null;
  const ext = path.split('.').pop()?.toLowerCase();
  if (!ext) return null;
  const mimeMap: Record<string, string> = {
    // Imagens
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    // PDF
    'pdf': 'application/pdf',
    // XML
    'xml': 'application/xml',
    // Documentos Office
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // CSV
    'csv': 'text/csv',
    // Google Sheets
    'gsheet': 'application/vnd.google-apps.spreadsheet',
    // DWG
    'dwg': 'application/dwg'
  };
  return mimeMap[ext] || null;
};

// Configurações por tipo de entidade
// Modos disponíveis:
// - 'catalog': Catálogo puro (sem entityId, busca por tipo em file)
// - 'junction': Vinculado via entidade_file (JOIN)
// - 'foreignKey': FK simples na tabela específica
// - 'polymorphic': LEGADO - usa entidade/entidade_id em file (para compatibilidade)
const ENTITY_CONFIG: Record<string, EntityConfigEntry> = {
  // ========== MODO JUNCTION (usa entidade_file) ==========
  'obra_jornada': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'obra_jornada',
    mode: 'junction',
    entidadeValue: 'obra_jornada',
    hasObraId: true,
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'obra_jornada_ata': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'obra_jornada_ata',
    mode: 'junction',
    entidadeValue: 'obra_jornada_ata',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'projeto': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'projeto',
    mode: 'junction',
    entidadeValue: 'projeto',
    hasObraId: true,
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'pessoa': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'pessoa',
    mode: 'junction',
    entidadeValue: 'pessoa',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'atividade': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'atividade',
    mode: 'junction',
    entidadeValue: 'atividade',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },

  // ========== MODO CATALOG (catálogo puro em file) ==========
  'proposta_imagem': {
    tableName: 'file',
    bucketName: 'documento',
    storagePath: 'proposta_imagem',
    mode: 'catalog',
    catalogFilter: { tipo: 'proposta_imagem' },
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  // LEGADO: manter proposta_grupo_imagem para compatibilidade durante migração
  'proposta_grupo_imagem': {
    tableName: 'proposta_grupo_imagem',
    bucketName: 'documento',
    storagePath: 'proposta_grupo_imagem',
    mode: 'catalog',
    catalogMode: true, // LEGADO
    storagePathField: 'imagem_url',
    originalNameField: null,
    descriptionField: 'descricao',
    mimeTypeField: null,
    sizeField: null
  },

  // ========== MODO FOREIGNKEY (FK simples) ==========
  'lancamento': {
    tableName: 'lancamento_anexo',
    bucketName: 'lancamento-anexos',
    mode: 'foreignKey',
    foreignKey: 'lancamento_id',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'movimento': {
    tableName: 'lancamento_anexo',
    bucketName: 'lancamento-anexos',
    mode: 'foreignKey',
    foreignKey: 'movimento_id',
    additionalForeignKey: 'lancamento_id',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'oportunidade': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'oportunidade',
    mode: 'junction',
    entidadeValue: 'oportunidade',
    hasObraId: true,
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },

  // ========== MODO JUNCTION - Suprimentos ==========
  'ordem_compra_orcamento': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'ordem_compra_orcamento',
    mode: 'junction',
    entidadeValue: 'ordem_compra_orcamento',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'ordem_compra': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'ordem_compra',
    mode: 'junction',
    entidadeValue: 'ordem_compra',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },

  // ========== MODO FOREIGNKEY - Documentos OC ==========
  'ordem_compra_documento': {
    tableName: 'ordem_compra_documento',
    bucketName: 'documento',
    storagePath: 'ordem_compra',
    mode: 'foreignKey',
    foreignKey: 'ordem_compra_id',
    storagePathField: 'documento_anexo',
    originalNameField: null,
    descriptionField: 'obs',
    mimeTypeField: null,
    sizeField: null,
    softDelete: true,
    selectOverride: '*, tipo:documento_tipo_id(id, documento_tipo)',
    customFields: [
      {
        key: 'documento_tipo_id',
        label: 'Tipo de Documento',
        type: 'select',
        required: true,
        optionsTable: 'ordem_compra_documento_tipo',
        optionsValueField: 'id',
        optionsLabelField: 'documento_tipo'
      },
      {
        key: 'documento_numero',
        label: 'Número do Documento',
        type: 'text',
        placeholder: 'Ex: 001.373.663'
      },
      {
        key: 'obs',
        label: 'Observação',
        type: 'text',
        placeholder: 'Opcional'
      }
    ],
    extraInsertData: (pessoaData) => ({
      documento_data_adicao: new Date().toISOString(),
      documento_user_adicao_id: pessoaData.id,
      filial_id: pessoaData.filial_id || null,
      updated_by: pessoaData.id
    }),
    listBadge: (anexo) => {
      const tipoRaw = anexo.tipo;
      const tipoNome =
        tipoRaw && typeof tipoRaw === 'object' && 'documento_tipo' in tipoRaw
          ? (tipoRaw as { documento_tipo?: string }).documento_tipo
          : undefined;
      if (!tipoNome) return null;
      const tipoColors: Record<string, { bg: string; color: string }> = {
        'NFe - Danfe PDF': { bg: '#dbeafe', color: '#1e40af' },
        'NFe - XML': { bg: '#e0e7ff', color: '#3730a3' },
        'NFSe - PDF': { bg: '#ede9fe', color: '#5b21b6' },
        'NFSe - XML': { bg: '#f3e8ff', color: '#6b21a8' },
        'Recibo': { bg: '#dcfce7', color: '#166534' },
        'Conhecimento de Transporte': { bg: '#ffedd5', color: '#9a3412' },
      };
      const tc = tipoColors[tipoNome] || { bg: '#f3f4f6', color: '#374151' };
      return { text: tipoNome, bg: tc.bg, color: tc.color };
    },
    listPrimary: (anexo) => {
      const numero = anexo.documento_numero;
      if (typeof numero === 'string' && numero) return `Nº ${numero}`;
      if (typeof numero === 'number') return `Nº ${numero}`;
      return null;
    },
    listSecondary: (anexo) => {
      const obs = anexo.obs;
      return typeof obs === 'string' && obs ? obs : null;
    }
  },

  // ========== MODO JUNCTION - Obra ==========
  'obra': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'obra',
    mode: 'junction',
    entidadeValue: 'obra',
    hasObraId: true,
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
  'obra_ambiente': {
    tableName: 'file',
    junctionTable: 'entidade_file',
    bucketName: 'documento',
    storagePath: 'obra_ambiente',
    mode: 'junction',
    entidadeValue: 'obra_ambiente',
    storagePathField: 'nome_storage',
    originalNameField: 'nome_original',
    descriptionField: 'descricao',
    mimeTypeField: 'mime_type',
    sizeField: 'tamanho_bytes'
  },
};

/**
 * GenericFile - Componente genérico para upload e gerenciamento de arquivos
 *
 * Props:
 * - entityId: string - ID da entidade pai (obrigatório exceto em catalogMode)
 * - entityType: string - Tipo da entidade (ex: 'obra_jornada', 'lancamento', 'movimento', 'proposta_grupo_imagem')
 * - additionalEntityId: string - ID adicional para tipos que requerem (ex: lancamento_id para movimento)
 * - readOnly: boolean - Se true, apenas visualização
 * - onChange: function(files) - Callback quando lista de arquivos mudar
 * - refreshKey: number - Quando muda, força reload dos arquivos
 * - recurso: string - Prefixo para checagem de permissões (DEPRECATED - use canCreate/canEdit/canDelete)
 * - compact: boolean - Se true, usa layout compacto (para uso em modais/cards menores)
 *
 * Props adicionais (para uso com GenericList):
 * - searchTerm: string - Filtro de busca (vem do GenericHeader)
 * - canCreate: boolean - Permite criar/upload (default: true)
 * - canEdit: boolean - Permite editar descrição (default: true)
 * - canDelete: boolean - Permite excluir (default: true)
 * - onDataChange: function(data) - Callback quando dados mudam (para atualizar contador no header)
 * - gridColumns: number - Quantidade de colunas no grid (default: 4)
 * - layout: 'grid' | 'list' - Modo de visualização (default: 'grid')
 * - deleteModalTitle: string - Título do modal de exclusão (default: "Excluir anexo")
 */
const GenericFile = forwardRef<GenericFileHandle, GenericFileProps>(function GenericFile({
  entityId,
  entityType,
  additionalEntityId,
  readOnly = false,
  onChange,
  refreshKey = 0,
  recurso,
  compact: _compact = false,
  // Novas props
  searchTerm = '',
  canCreate: canCreateProp,
  canEdit: canEditProp,
  canDelete: canDeleteProp,
  onDataChange,
  gridColumns = 4,
  layout = 'grid',
  deleteModalTitle = 'Excluir anexo',
  showArchived = false
}: GenericFileProps, ref: ForwardedRef<GenericFileHandle>) {
  void _compact;
  const { hasPermission, pessoa } = useAuth();
  const { showError, showSuccess, showProgress, updateToast } = useToast();

  // Obter configuração da entidade (antes dos hooks, mas só para referência)
  const config: EntityConfigEntry | undefined = entityType ? ENTITY_CONFIG[entityType] : undefined;

  // Catálogo de file (proposta_imagem): habilita arquivamento/edição inline.
  // Restrito a mode 'catalog' + tabela file para não vazar as ações novas aos
  // ~10 consumidores junction/foreignKey do GenericFile.
  const isFileCatalog = config?.mode === 'catalog' && config?.tableName === 'file';

  // TODOS os hooks DEVEM ser chamados antes de qualquer return condicional
  const [anexos, setAnexos] = useState<GenericFileRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const activeUploadsRef = useRef<Record<string, ActiveUpload | undefined>>({});

  // Cleanup: abortar uploads ativos ao desmontar componente
  useEffect(() => {
    return () => {
      Object.values(activeUploadsRef.current).forEach(upload => {
        if (upload?.abort) upload.abort();
      });
      activeUploadsRef.current = {};
    };
  }, []);

  // Estado para thumbnails de imagens
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});

  // Estados para modal de adicionar arquivo (usando FileEditModal)
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [pendingAddFiles, setPendingAddFiles] = useState<File[]>([]);
  const [addingFile, setAddingFile] = useState<boolean>(false);

  // Estados para modal de confirmação de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [anexoToDelete, setAnexoToDelete] = useState<GenericFileRow | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Estados para modal de edição de descrição
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingAnexo, setEditingAnexo] = useState<GenericFileRow | null>(null);
  const [editDescricao, setEditDescricao] = useState<string>('');
  const [editCategoriaId, setEditCategoriaId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  // Catálogo de file: confirmação da troca de arquivo (nomeia propostas afetadas
  // antes de aplicar, pois a troca atinge a visualização ao vivo das propostas).
  const [showReplaceConfirm, setShowReplaceConfirm] = useState<boolean>(false);
  const [pendingEditPayload, setPendingEditPayload] = useState<FileEditPayload | null>(null);

  // Catálogo de file: confirmação ao arquivar uma imagem.
  const [showArchiveConfirm, setShowArchiveConfirm] = useState<boolean>(false);
  const [pendingArchive, setPendingArchive] = useState<GenericFileRow | null>(null);
  const [archiving, setArchiving] = useState<boolean>(false);

  // Estado para categorias de arquivo (usado apenas em modo junction para projeto)
  const [categorias, setCategorias] = useState<CategoriaRow[]>([]);

  // Catálogo de file: contagem por tenant de propostas que referenciam cada
  // imagem (via RPC SECDEF fn_proposta_imagem_ref_counts — contagem real,
  // independente do que o curador acessa). Alimenta a guarda de exclusão (U4)
  // e a confirmação de troca/arquivamento. A FK NO ACTION segue como rede final.
  const [refCounts, setRefCounts] = useState<Record<string, number>>({});

  // Verificar permissões
  // Novas props têm prioridade, depois recurso, depois default true
  // V3: ações em minúsculo - create, edit, delete
  const canInsert = canCreateProp !== undefined ? canCreateProp :
    (!recurso || hasPermission(recurso, 'create'));
  const canDeleteFinal = canDeleteProp !== undefined ? canDeleteProp :
    (!recurso || hasPermission(recurso, 'delete'));
  // Espelha canDeleteFinal: Editar/Arquivar/Restaurar exigem permissão EDIT.
  const canEditFinal = canEditProp !== undefined ? canEditProp :
    (!recurso || hasPermission(recurso, 'edit'));

  // Expor método openUpload via ref para controle externo
  useImperativeHandle(ref, () => ({
    openUpload: () => fileInputRef.current?.click(),
    reload: () => loadAnexos()
  }));

  useEffect(() => {
    // Em catalog mode, carrega sem precisar de entityId
    // Em modo normal, precisa de entityId
    const isCatalog = config?.mode === 'catalog' || config?.catalogMode;
    if (config && (isCatalog || entityId)) {
      loadAnexos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, refreshKey, config]);

  // Carregar thumbnails para imagens (em paralelo para performance)
  useEffect(() => {
    if (!config) return;

    const loadThumbnails = async (): Promise<void> => {
      // Filtrar apenas imagens que ainda não têm thumbnail
      const imageAnexos = anexos.filter((a: GenericFileRow) => {
        if (thumbnailUrls[a.id]) return false; // já tem thumbnail
        const rawMime = config.mimeTypeField ? a[config.mimeTypeField] : undefined;
        const storagePath = a[config.storagePathField];
        const pathStr = typeof storagePath === 'string' ? storagePath : null;
        const mimeType =
          (typeof rawMime === 'string' ? rawMime : null) ||
          getMimeTypeFromPath(pathStr);
        return mimeType?.startsWith('image/') ?? false;
      });

      if (imageAnexos.length === 0) return;

      // Carregar todas as URLs em paralelo (muito mais rápido)
      const results = await Promise.all(
        imageAnexos.map(async (anexo: GenericFileRow) => {
          try {
            const storagePathRaw = anexo[config.storagePathField];
            const storagePath = typeof storagePathRaw === 'string' ? storagePathRaw : null;
            if (!storagePath) return null;

            const { data, error } = await supabase.storage
              .from(config.bucketName)
              .createSignedUrl(storagePath, 3600, {
                transform: {
                  width: 200,
                  height: 200,
                  resize: 'contain',
                  quality: 80
                }
              });

            if (!error && data) {
              return { id: anexo.id, url: data.signedUrl };
            }
          } catch (err) {
            console.error('Erro ao carregar thumbnail:', err);
          }
          return null;
        })
      );

      const urls: Record<string, string> = {};
      results.forEach(result => {
        if (result) urls[result.id] = result.url;
      });

      if (Object.keys(urls).length > 0) {
        setThumbnailUrls(prev => ({ ...prev, ...urls }));
      }
    };

    if (anexos.length > 0) {
      loadThumbnails();
    }
  }, [anexos, config, thumbnailUrls]);

  useEffect(() => {
    if (onChange) {
      onChange(anexos);
    }
  }, [anexos, onChange]);

  // Carregar categorias de arquivo (para entidades com obra_id)
  useEffect(() => {
    const shouldLoadCategorias = !!config?.hasObraId;
    if (!shouldLoadCategorias) return;

    const loadCategorias = async (): Promise<void> => {
      try {
        const { data, error } = await supabase
          .from('file_categoria')
          .select('id, nome, ordem')
          .eq('ativo', true)
          .is('deleted_at', null)
          .order('ordem', { ascending: true });

        if (error) throw error;
        setCategorias((data ?? []) as CategoriaRow[]);
      } catch (err) {
        console.error('[GenericFile] Erro ao carregar categorias:', err);
      }
    };

    loadCategorias();
  }, [config, entityType]);

  // Estado e loader para opções de custom fields (select)
  const [customFieldOptions, setCustomFieldOptions] = useState<Record<string, Array<{ value: unknown; label: string }>>>({});

  useEffect(() => {
    if (!config?.customFields) return;
    const selectFields = config.customFields.filter(f => f.type === 'select' && f.optionsTable);
    if (selectFields.length === 0) return;

    const loadOptions = async (): Promise<void> => {
      const results: Record<string, Array<{ value: unknown; label: string }>> = {};
      await Promise.all(selectFields.map(async (field: EntityFieldConfig) => {
        try {
          if (!field.optionsTable || !field.optionsValueField || !field.optionsLabelField) return;
          // Supabase dynamic-table bypass (mold B.2) — runtime table names can't satisfy generated literal-union overload.
          const dynDb = supabase as unknown as {
            from: (t: string) => { select: (s: string) => { order: (c: string) => Promise<{ data: Array<Record<string, unknown>> | null }> } };
          };
          const query = dynDb
            .from(field.optionsTable)
            .select(`${field.optionsValueField}, ${field.optionsLabelField}`)
            .order(field.optionsLabelField);
          const { data } = await query;
          if (data) {
            results[field.key] = data.map(row => ({
              value: row[field.optionsValueField as string],
              label: String(row[field.optionsLabelField as string] ?? '')
            }));
          }
        } catch (err) {
          console.error(`[GenericFile] Erro ao carregar opções para ${field.key}:`, err);
        }
      }));
      setCustomFieldOptions(results);
    };
    loadOptions();
  }, [config]);

  // Resolver customFields com opções carregadas
  const resolvedCustomFields = useMemo(() => {
    if (!config?.customFields) return [];
    return config.customFields.map(field => {
      if (field.type === 'select' && field.optionsTable) {
        return { ...field, options: customFieldOptions[field.key] || [] };
      }
      return field;
    });
  }, [config, customFieldOptions]);

  // Valores dos custom fields do anexo em edição (para edit modal)
  const editCustomFieldValues = useMemo<Record<string, unknown>>(() => {
    if (!editingAnexo || !config?.customFields) return {};
    const values: Record<string, unknown> = {};
    config.customFields.forEach((f: EntityFieldConfig) => {
      values[f.key] = editingAnexo[f.key] || '';
    });
    return values;
  }, [editingAnexo, config]);

  // Chave estável do conjunto de ids: muda só quando entra/sai imagem (não em
  // arquivar/restaurar/editar), evitando recontar referências à toa.
  const anexoIdsKey = useMemo(() => anexos.map(a => a.id).join(','), [anexos]);

  // Carrega a contagem de referências em proposta_grupo para o catálogo de file.
  useEffect(() => {
    if (!isFileCatalog) return;
    const ids = anexoIdsKey ? anexoIdsKey.split(',') : [];
    if (ids.length === 0) {
      setRefCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // RPC SECURITY DEFINER: contagem real por tenant (ignora
        // fn_can_access_proposta), para o guard refletir o uso mesmo quando o
        // curador não acessa as propostas que referenciam a imagem.
        const dynRpc = supabase as unknown as {
          rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: Array<{ file_id: string; cnt: number }> | null; error: unknown }>;
        };
        const { data, error } = await dynRpc.rpc('fn_proposta_imagem_ref_counts', { p_file_ids: ids });
        if (error) throw error;
        const counts: Record<string, number> = {};
        (data ?? []).forEach((row) => {
          if (row.file_id) counts[row.file_id] = Number(row.cnt) || 0;
        });
        if (!cancelled) setRefCounts(counts);
      } catch (err) {
        // Em falha, a FK NO ACTION continua sendo a rede autoritativa.
        console.error('[GenericFile] Erro ao contar referências de proposta:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFileCatalog, anexoIdsKey]);

  // Filtrar anexos pelo searchTerm (e, no catálogo de file, pelas arquivadas)
  const filteredAnexos = useMemo<GenericFileRow[]>(() => {
    if (!config) return anexos;
    let list = anexos;
    // Catálogo de file: oculta arquivadas por padrão (R10); o toggle revela.
    // Filtro em memória — o catálogo é pequeno, então alternar não refaz query.
    if (isFileCatalog && !showArchived) {
      list = list.filter(a => !a.arquivado_em);
    }
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(a => {
      const descField = config.descriptionField ? a[config.descriptionField] : undefined;
      const nameField = config.originalNameField ? a[config.originalNameField] : undefined;
      const pathField = a[config.storagePathField];
      const desc = typeof descField === 'string' ? descField.toLowerCase() : '';
      const nameFromOrig = typeof nameField === 'string' ? nameField.toLowerCase() : '';
      const nameFromPath =
        typeof pathField === 'string'
          ? (pathField.split('/').pop() || '').toLowerCase()
          : '';
      const name = nameFromOrig || nameFromPath;
      return desc.includes(term) || name.includes(term);
    });
  }, [anexos, searchTerm, config, isFileCatalog, showArchived]);

  // Verificar configuração da entidade DEPOIS de todos os hooks
  if (!config) {
    console.error(`[GenericFile] Configuração não encontrada para entityType: ${entityType}`);
    return <div style={{ color: 'var(--danger)', padding: '16px' }}>Erro: Tipo de entidade não configurado</div>;
  }

  // Supabase dynamic-table bypass (mold B.2). Builder shape intentionally permissive
  // because Supabase's typed overloads require literal-union table names, which this
  // dispatcher can't satisfy at call-site.
  type DynBuilder = {
    select: (s?: string) => DynBuilder;
    eq: (col: string, val: unknown) => DynBuilder;
    is: (col: string, val: unknown) => DynBuilder;
    order: (c: string, o: { ascending: boolean }) => Promise<{ data: GenericFileRow[] | null; error: unknown }> & DynBuilder;
    insert: (values: unknown) => DynBuilder;
    update: (values: Record<string, unknown>) => DynBuilder;
    delete: () => DynBuilder;
    single: () => Promise<{ data: GenericFileRow | null; error: unknown }>;
  } & Promise<{ data: GenericFileRow[] | null; error: unknown }>;
  const dynDb = supabase as unknown as { from: (t: string) => DynBuilder };

  const loadAnexos = async (): Promise<void> => {
    if (!config) return;
    // Em catalog mode, carrega todos do catálogo (sem entityId)
    // Em junction/foreignKey mode, precisa de entityId
    const isCatalog = config.mode === 'catalog' || config.catalogMode;
    if (!isCatalog && !entityId) return;

    setLoading(true);
    try {
      let data: GenericFileRow[] = [];

      switch (config.mode) {
        case 'catalog': {
          // CATÁLOGO: busca direto em file com filtro por tipo
          let query: DynBuilder = dynDb.from(config.tableName).select('*');

          // Aplicar filtros do catálogo (ex: tipo = 'proposta_imagem')
          if (config.catalogFilter) {
            Object.entries(config.catalogFilter).forEach(([key, value]) => {
              query = query.eq(key, value);
            });
          }

          // Soft delete (se tabela suportar)
          if (config.tableName === 'file') {
            query = query.is('deleted_at', null);
          }

          const { data: catalogData, error } = await query.order('created_at', { ascending: false });
          if (error) throw error;
          data = catalogData || [];
          break;
        }

        case 'junction': {
          // JUNCTION: buscar via entidade_file (arquitetura atual)
          const { data: junctionData, error: junctionError } = await supabase
            .from('entidade_file')
            .select(`
              id,
              file_id,
              papel,
              ordem,
              categoria_id,
              created_at,
              file:file_id (
                id,
                nome_original,
                nome_storage,
                descricao,
                mime_type,
                tamanho_bytes,
                tipo,
                created_at
              )
            `)
            .eq('entidade', config.entidadeValue ?? '')
            .eq('entidade_id', entityId ?? '')
            .is('deleted_at', null)
            .order('created_at', { ascending: false });

          if (junctionError) throw junctionError;

          // Flatten: extrair file de cada junction. Row shape is open-record from PostgREST.
          type JunctionRow = {
            id: string;
            file?: Record<string, unknown> | null;
            papel?: string | null;
            ordem?: number | null;
            categoria_id?: string | null;
          };
          data = ((junctionData ?? []) as unknown as JunctionRow[])
            .filter(j => j.file)
            .map(j => ({
              ...(j.file as Record<string, unknown>),
              _junctionId: j.id,
              _papel: j.papel,
              _ordem: j.ordem,
              _categoriaId: j.categoria_id
            })) as unknown as GenericFileRow[];
          break;
        }

        case 'foreignKey':
        default: {
          // FK SIMPLES: mantém lógica original
          let query: DynBuilder = dynDb.from(config.tableName).select(config.selectOverride || '*');

          if (config.foreignKey) {
            query = query.eq(config.foreignKey, entityId);
          }

          if (config.softDelete) {
            query = query.is('deleted_at', null);
          }

          const { data: fkData, error } = await query.order('created_at', { ascending: false });
          if (error) throw error;
          data = fkData ?? [];
          break;
        }
      }

      setAnexos(data);

      // Notificar container sobre mudança de dados
      if (onDataChange) {
        onDataChange(data);
      }
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
      showError({ title: 'Erro', description: 'Erro ao carregar anexos.' });
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file: File): string | null => {
    const maxSize = config?.maxFileSize || MAX_FILE_SIZE;
    if (file.size > maxSize) {
      const maxMB = Math.round(maxSize / (1024 * 1024));
      return `Arquivo muito grande. Tamanho máximo: ${maxMB}MB`;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return `Tipo de arquivo não permitido. Permitidos: ${ALLOWED_EXTENSIONS.join(', ')}`;
      }
    }

    return null;
  };

  // Resolve the effective MIME type: if the browser reports a generic type
  // (empty or application/octet-stream), derive it from the file extension.
  const resolveContentType = (file: File): string => {
    if (file.type && file.type !== 'application/octet-stream') return file.type;
    return getMimeTypeFromPath(file.name) || 'application/octet-stream';
  };

  // Re-wrap a File with the correct MIME type when the browser mis-detects it.
  const withCorrectMime = (file: File): File => {
    const correctType = resolveContentType(file);
    if (file.type === correctType) return file;
    return new File([file], file.name, { type: correctType, lastModified: file.lastModified });
  };

  const uploadFile = async (
    file: File,
    descricao: string | null = null,
    categoriaId: string | null = null,
    customData: Record<string, unknown> | null = null
  ): Promise<void> => {
    const validationError = validateFile(file);
    if (validationError) {
      showError({ title: 'Arquivo inválido', description: validationError });
      return;
    }

    // Em catalog mode não precisa de entityId
    // Em junction/foreignKey mode, precisa de entityId
    const isCatalog = config.mode === 'catalog' || config.catalogMode;
    if (!isCatalog && !entityId) {
      showError({ title: 'Ação não permitida', description: 'Salve o registro antes de adicionar anexos.' });
      return;
    }

    setUploading(true);

    try {
      // Local session, not getUser(): a refresh cooldown must not read as signed out (JOSH-89).
      const user = await getSessionUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Buscar pessoa pelo user_id
      const { data: pessoaData, error: pessoaError } = await supabase
        .from('pessoa')
        .select('id, tenant_id')
        .eq('user_id', user.id)
        .single();

      if (isReconnectingError(pessoaError)) throw new ReconnectingError(pessoaError);
      if (pessoaError || !pessoaData) {
        throw new Error('Usuário não encontrado');
      }

      // Gerar nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      // Em catalog mode, usa UUID direto; em outros modos, usa entityId como pasta
      const baseFileName = isCatalog
        ? `${crypto.randomUUID()}.${fileExt}`
        : `${entityId}/${crypto.randomUUID()}.${fileExt}`;
      // Se houver storagePath definido, usar como prefixo (com tenant isolation)
      const tenantId = pessoa?.tenant_id;
      const fileName = config.storagePath
        ? `${config.storagePath}/${tenantId}/${baseFileName}`
        : baseFileName;

      // Upload para o bucket (re-wrap file with correct MIME for Supabase SDK)
      const uploadFile_ = withCorrectMime(file);
      const effectiveType = resolveContentType(file);
      const { error: uploadError } = await supabase.storage
        .from(config.bucketName)
        .upload(fileName, uploadFile_, { contentType: effectiveType });

      if (uploadError) throw uploadError;

      // Preparar dados para inserção na tabela file
      const insertData: Record<string, unknown> = {
        [config.storagePathField]: fileName,
        created_by: pessoaData.id,
        tenant_id: pessoaData.tenant_id
      };

      // Campos opcionais
      if (config.originalNameField) {
        insertData[config.originalNameField] = file.name;
      }
      if (config.mimeTypeField) {
        insertData[config.mimeTypeField] = effectiveType;
      }
      if (config.sizeField) {
        insertData[config.sizeField] = file.size;
      }
      // Custom fields: merge todos os valores custom no insertData
      if (customData) {
        Object.entries(customData).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            insertData[key] = value;
          }
        });
      } else if (config.descriptionField && descricao) {
        insertData[config.descriptionField] = descricao;
      }

      // Extra insert data do config (ex: documento_data_adicao, documento_user_adicao_id)
      if (config.extraInsertData) {
        Object.assign(insertData, config.extraInsertData(pessoaData));
      }

      // Adicionar tipo se for catálogo com catalogFilter
      if (config.catalogFilter?.tipo) {
        insertData.tipo = config.catalogFilter.tipo;
      }

      let anexoData: GenericFileRow | null = null;

      switch (config.mode) {
        case 'catalog': {
          // CATÁLOGO: insere só em file (sem vínculo)
          const { data, error } = await dynDb
            .from(config.tableName)
            .insert(insertData)
            .select()
            .single();

          if (error) {
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw error;
          }
          anexoData = data;
          break;
        }

        case 'junction': {
          // JUNCTION: insere em file + entidade_file (arquitetura atual)
          // 1. Inserir em file (sem campos de entidade)
          const { data: fileData, error: fileError } = await dynDb
            .from(config.tableName)
            .insert(insertData)
            .select()
            .single();

          if (fileError || !fileData) {
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw fileError || new Error('Insert failed');
          }

          // 2. Criar vínculo em entidade_file (sem tenant_id - herda de file)
          const junctionInsert: Record<string, unknown> = {
            file_id: fileData.id,
            entidade: config.entidadeValue,
            entidade_id: entityId,
            papel: 'principal',
            created_by: pessoaData.id
          };
          // Adicionar categoria se fornecida
          if (categoriaId) {
            junctionInsert.categoria_id = categoriaId;
          }

          // entidade_file is a typed table but junctionInsert is dynamic-shape (categoria_id added conditionally).
          // Single-step cast at the insert boundary to bypass PostgREST overload resolution.
          const dynJunctionDb = supabase as unknown as {
            from: (t: 'entidade_file') => {
              insert: (v: Record<string, unknown>) => {
                select: (s: string) => { single: () => Promise<{ data: { id: string; categoria_id: string | null } | null; error: unknown }> };
              };
            };
          };
          const { data: junctionData, error: junctionError } = await dynJunctionDb
            .from('entidade_file')
            .insert(junctionInsert)
            .select('id, categoria_id')
            .single();

          if (junctionError) {
            // Se falhar o vínculo, remover o arquivo criado
            await dynDb.from(config.tableName).delete().eq('id', fileData.id);
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw junctionError;
          }

          // Retornar com _junctionId e categoria_id para funcionar corretamente
          anexoData = {
            ...fileData,
            _junctionId: junctionData?.id ?? null,
            _categoriaId: junctionData?.categoria_id ?? null
          } as GenericFileRow;
          break;
        }

        case 'foreignKey':
        default: {
          // FK SIMPLES: mantém lógica original
          if (config.foreignKey) {
            insertData[config.foreignKey] = entityId;
          }

          // Campo adicional (ex: lancamento_id para anexos de movimento)
          if (config.additionalForeignKey && additionalEntityId) {
            insertData[config.additionalForeignKey] = additionalEntityId;
          }

          const { data, error } = await dynDb
            .from(config.tableName)
            .insert(insertData)
            .select()
            .single();

          if (error) {
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw error;
          }
          anexoData = data;
          break;
        }
      }

      // Adicionar à lista local
      if (anexoData) {
        setAnexos(prev => [anexoData, ...prev]);
      }

    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      const { title, description } = getErrorMessage(error);
      showError({ title, description });
    } finally {
      setUploading(false);
    }
  };

  const uploadFileResumable = async (
    file: File,
    descricao: string | null = null,
    categoriaId: string | null = null,
    customData: Record<string, unknown> | null = null
  ): Promise<void> => {
    const validationError = validateFile(file);
    if (validationError) {
      showError({ title: 'Arquivo inválido', description: validationError });
      return;
    }

    const isCatalog = config.mode === 'catalog' || config.catalogMode;
    if (!isCatalog && !entityId) {
      showError({ title: 'Ação não permitida', description: 'Salve o registro antes de adicionar anexos.' });
      return;
    }

    setUploading(true);
    try {
      // Local session, not getUser(): a refresh cooldown must not read as signed out (JOSH-89).
      const user = await getSessionUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: pessoaData, error: pessoaError } = await supabase
        .from('pessoa')
        .select('id, tenant_id')
        .eq('user_id', user.id)
        .single();

      if (isReconnectingError(pessoaError)) throw new ReconnectingError(pessoaError);
      if (pessoaError || !pessoaData) throw new Error('Usuário não encontrado');

      const fileExt = file.name.split('.').pop();
      const baseFileName = isCatalog
        ? `${crypto.randomUUID()}.${fileExt}`
        : `${entityId}/${crypto.randomUUID()}.${fileExt}`;
      const tenantId = pessoa?.tenant_id;
      const fileName = config.storagePath
        ? `${config.storagePath}/${tenantId}/${baseFileName}`
        : baseFileName;

      let lastBytes = 0;
      let lastTime = Date.now();
      let currentSpeed = 0;

      const toastId = showProgress({
        title: 'Enviando arquivo',
        description: `${file.name}\n0%`
      }, {
        onCancel: () => {
          const activeUpload = activeUploadsRef.current[toastId];
          if (activeUpload) {
            activeUpload.abort();
            delete activeUploadsRef.current[toastId];
          }
        }
      });

      const uploadFile_ = withCorrectMime(file);
      const effectiveType = resolveContentType(file);
      const { promise, abort } = await resumableUpload({
        bucketName: config.bucketName,
        storagePath: fileName,
        file: uploadFile_,
        contentType: effectiveType,
        onProgress: (percentage, bytesUploaded, bytesTotal) => {
          const now = Date.now();
          const elapsed = (now - lastTime) / 1000;
          if (elapsed > 0.5) {
            currentSpeed = (bytesUploaded - lastBytes) / elapsed;
            lastBytes = bytesUploaded;
            lastTime = now;
          }

          const speedText = currentSpeed > 0 ? ` · ${formatSpeed(currentSpeed)}` : '';
          updateToast(toastId, {
            progress: percentage,
            description: `${file.name}\n${percentage.toFixed(0)}%${speedText}`
          });
        },
        onSuccess: () => {
          delete activeUploadsRef.current[toastId];
        },
        onError: (error: { message?: string } | Error | unknown) => {
          delete activeUploadsRef.current[toastId];
          const msg = error instanceof Error ? error.message : (error && typeof error === 'object' && 'message' in error ? String((error as { message?: unknown }).message ?? '') : '');
          updateToast(toastId, {
            type: 'error',
            title: 'Erro no upload',
            description: msg || 'Falha ao enviar arquivo',
            progress: undefined,
            autoClose: true,
            duration: 5000
          });
        }
      });

      activeUploadsRef.current[toastId] = { abort };

      await promise;

      const insertData: Record<string, unknown> = {
        [config.storagePathField]: fileName,
        created_by: pessoaData.id,
        tenant_id: pessoaData.tenant_id
      };

      if (config.originalNameField) insertData[config.originalNameField] = file.name;
      if (config.mimeTypeField) insertData[config.mimeTypeField] = effectiveType;
      if (config.sizeField) insertData[config.sizeField] = file.size;

      if (customData) {
        Object.entries(customData).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            insertData[key] = value;
          }
        });
      } else if (config.descriptionField && descricao) {
        insertData[config.descriptionField] = descricao;
      }

      if (config.extraInsertData) {
        Object.assign(insertData, config.extraInsertData(pessoaData));
      }

      if (config.catalogFilter?.tipo) {
        insertData.tipo = config.catalogFilter.tipo;
      }

      let anexoData: GenericFileRow | null = null;

      switch (config.mode) {
        case 'catalog': {
          const { data, error } = await dynDb
            .from(config.tableName)
            .insert(insertData)
            .select()
            .single();
          if (error) {
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw error;
          }
          anexoData = data;
          break;
        }
        case 'junction': {
          const { data: fileData, error: fileError } = await dynDb
            .from(config.tableName)
            .insert(insertData)
            .select()
            .single();
          if (fileError || !fileData) {
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw fileError || new Error('Insert failed');
          }

          const junctionInsert: Record<string, unknown> = {
            file_id: fileData.id,
            entidade: config.entidadeValue,
            entidade_id: entityId,
            papel: 'principal',
            created_by: pessoaData.id
          };
          if (categoriaId) junctionInsert.categoria_id = categoriaId;

          // entidade_file typed-table; dynamic junctionInsert shape — single-step cast at boundary.
          const dynJunctionDb = supabase as unknown as {
            from: (t: 'entidade_file') => {
              insert: (v: Record<string, unknown>) => {
                select: (s: string) => { single: () => Promise<{ data: { id: string; categoria_id: string | null } | null; error: unknown }> };
              };
            };
          };
          const { data: junctionData, error: junctionError } = await dynJunctionDb
            .from('entidade_file')
            .insert(junctionInsert)
            .select('id, categoria_id')
            .single();

          if (junctionError) {
            await dynDb.from(config.tableName).delete().eq('id', fileData.id);
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw junctionError;
          }

          anexoData = {
            ...fileData,
            _junctionId: junctionData?.id ?? null,
            _categoriaId: junctionData?.categoria_id ?? null
          } as GenericFileRow;
          break;
        }
        case 'foreignKey':
        default: {
          if (config.foreignKey) insertData[config.foreignKey] = entityId;
          if (config.additionalForeignKey && additionalEntityId) {
            insertData[config.additionalForeignKey] = additionalEntityId;
          }

          const { data, error } = await dynDb
            .from(config.tableName)
            .insert(insertData)
            .select()
            .single();
          if (error) {
            await supabase.storage.from(config.bucketName).remove([fileName]);
            throw error;
          }
          anexoData = data;
          break;
        }
      }

      if (anexoData) {
        setAnexos(prev => [anexoData, ...prev]);
      }

      updateToast(toastId, {
        type: 'success',
        title: 'Upload concluído',
        description: file.name,
        progress: 100,
        autoClose: true,
        duration: 3000
      });

    } catch (error) {
      // Cancelamento pelo usuário não é erro — ignora silenciosamente
      const errMsg = error instanceof Error ? error.message : '';
      if (errMsg !== 'Upload cancelado') {
        console.error('[GenericFile] Erro no upload resumível:', error);
      }
    } finally {
      setUploading(false);
    }
  };

  // Abre o FileEditModal em modo add para adicionar arquivos
  const openAddModal = (files: File[]): void => {
    if (!files || files.length === 0) return;

    // Validar todos os arquivos
    const validFiles = [];
    for (const file of files) {
      const validationError = validateFile(file);
      if (validationError) {
        showError({ title: 'Arquivo inválido', description: validationError });
        return;
      }
      validFiles.push(file);
    }

    setPendingAddFiles(validFiles);
    setShowAddModal(true);
  };

  // Handler quando o usuário confirma no FileEditModal (modo add)
  // Recebe array: [{ file, descricao, categoriaId }] ou [{ file, customData }]
  const handleAddSave = async (filesWithData: Array<{ file: File; descricao?: string | null; categoriaId?: string | null; customData?: Record<string, unknown> | null }>): Promise<void> => {
    if (!filesWithData || filesWithData.length === 0) return;

    setAddingFile(true);

    try {
      const maxSize = config.maxFileSize || MAX_FILE_SIZE;
      const useResumable = maxSize > RESUMABLE_THRESHOLD;

      for (const item of filesWithData) {
        const isResumable = useResumable && item.file.size > RESUMABLE_THRESHOLD;

        if (isResumable) {
          // Upload resumable: fechar modal imediatamente, toast cuida do progresso
          setShowAddModal(false);
          setPendingAddFiles([]);
          setAddingFile(false);

          if (item.customData) {
            uploadFileResumable(item.file, null, null, item.customData);
          } else {
            uploadFileResumable(item.file, item.descricao || null, item.categoriaId || null);
          }
        } else {
          // Upload padrão: aguarda conclusão
          if (item.customData) {
            await uploadFile(item.file, null, null, item.customData);
          } else {
            await uploadFile(item.file, item.descricao || null, item.categoriaId || null);
          }
        }
      }
      setShowAddModal(false);
      setPendingAddFiles([]);
    } finally {
      setAddingFile(false);
    }
  };

  const cancelAdd = () => {
    setShowAddModal(false);
    setPendingAddFiles([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const fileList = e.target.files;
    const files: File[] = fileList ? Array.from(fileList) : [];
    if (files.length > 0) {
      openAddModal(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);

    const files: File[] = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      openAddModal(files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDelete = (anexo: GenericFileRow): void => {
    setAnexoToDelete(anexo);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAnexoToDelete(null);
  };

  const confirmDelete = async () => {
    if (!anexoToDelete) return;

    setDeleting(true);

    try {
      // Obter pessoa para soft delete
      // Local session, not getUser(): a refresh cooldown must not drop the
      // deleted_by attribution (JOSH-89).
      const user = await getSessionUser();
      let pessoaId = null;
      if (user) {
        const { data: pessoaData, error: pessoaError } = await supabase
          .from('pessoa')
          .select('id')
          .eq('user_id', user.id)
          .single();
        if (isReconnectingError(pessoaError)) throw new ReconnectingError(pessoaError);
        pessoaId = pessoaData?.id;
      }

      const storagePathRaw = anexoToDelete[config.storagePathField];
      const storagePath = typeof storagePathRaw === 'string' ? storagePathRaw : null;

      // Supabase RPC bypass for dynamically-named RPC + nullable pessoaId param.
      const dynRpcDb = supabase as unknown as {
        rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }>;
      };

      switch (config.mode) {
        case 'junction': {
          // JUNCTION: deletar vínculo (soft delete se tiver _junctionId)
          if (anexoToDelete._junctionId) {
            // Soft delete no vínculo usando RPC (bypassa RLS)
            const { data: deleted, error: junctionError } = await dynRpcDb
              .rpc('fn_soft_delete_entidade_file', {
                p_entidade_file_id: anexoToDelete._junctionId,
                p_deleted_by: pessoaId
              });

            if (junctionError) {
              console.error('[GenericFile] Erro ao soft delete entidade_file:', junctionError);
              throw junctionError;
            }


            // Verificar se arquivo ainda tem vínculos ativos
            const { count } = await supabase
              .from('entidade_file')
              .select('*', { count: 'exact', head: true })
              .eq('file_id', anexoToDelete.id)
              .is('deleted_at', null);

            // Se não tem mais vínculos, soft delete no arquivo também
            if (count === 0) {
              // Soft delete no arquivo usando RPC (bypassa RLS)
              const { data: fileDeleted, error: fileError } = await dynRpcDb
                .rpc('fn_soft_delete_file', {
                  p_file_id: anexoToDelete.id,
                  p_deleted_by: pessoaId
                });

              if (fileError) {
                console.error('[GenericFile] Erro ao soft delete file:', fileError);
                throw fileError;
              }


              // Deletar do storage
              if (storagePath) {
                await supabase.storage.from(config.bucketName).remove([storagePath]);
              }
            }
          } else {
            // FALLBACK LEGADO: deletar direto (se não veio de junction)
            const { error: deleteError } = await dynDb
              .from(config.tableName)
              .update({
                deleted_at: new Date().toISOString(),
                deleted_by: pessoaId
              })
              .eq('id', anexoToDelete.id);

            if (deleteError) throw deleteError;

            // Deletar do storage
            if (storagePath) {
              await supabase.storage.from(config.bucketName).remove([storagePath]);
            }
          }
          break;
        }

        case 'catalog':
        case 'foreignKey':
        default: {
          // CATALOG/FK: deletar registro do banco
          let deleteError;
          if (config.softDelete) {
            // Soft delete: marcar como deletado
            const { error } = await dynDb
              .from(config.tableName)
              .update({
                deleted_at: new Date().toISOString(),
                deleted_by: pessoaId,
                updated_by: pessoaId
              })
              .eq('id', anexoToDelete.id);
            deleteError = error;
          } else {
            // Hard delete
            const { error } = await dynDb
              .from(config.tableName)
              .delete()
              .eq('id', anexoToDelete.id);
            deleteError = error;
          }

          if (deleteError) {
            console.error('Erro ao deletar registro:', deleteError);
            const m = deleteError && typeof deleteError === 'object' && 'message' in deleteError
              ? String((deleteError as { message?: unknown }).message ?? '')
              : '';
            throw new Error(m || 'Erro ao excluir registro do banco');
          }

          // Deletar do storage
          if (storagePath) {
            const { error: storageError } = await supabase.storage
              .from(config.bucketName)
              .remove([storagePath]);

            if (storageError) {
              console.warn('Aviso: arquivo pode ter ficado órfão no storage:', storageError);
            }
          }
          break;
        }
      }

      // Remover da lista local
      setAnexos(prev => prev.filter(a => a.id !== anexoToDelete.id));

      setShowDeleteModal(false);
      setAnexoToDelete(null);

    } catch (error) {
      console.error('Erro ao excluir anexo:', error);
      // Catálogo de file: violação de FK (imagem referenciada por proposta_grupo)
      // por corrida com a contagem RLS-escopada → mensagem orientando a arquivar.
      const code = error && typeof error === 'object' && 'code' in error
        ? String((error as { code?: unknown }).code ?? '') : '';
      const msg = error && typeof error === 'object' && 'message' in error
        ? String((error as { message?: unknown }).message ?? '') : '';
      const isFkViolation = code === '23503' || /foreign key/i.test(msg);
      if (isFileCatalog && isFkViolation) {
        showError({
          title: 'Imagem em uso',
          description: 'Esta imagem está vinculada a propostas. Arquive em vez de excluir.'
        });
      } else {
        const { title, description } = getErrorMessage(error);
        showError({ title, description });
      }
      setShowDeleteModal(false);
      setAnexoToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // Catálogo de file: UPDATE direto de arquivar/restaurar (mirror do
  // handleSaveEdit). file_update tem WITH CHECK true e o trigger de storage só
  // observa deleted_at, então não dispara reaping nem altera RLS. Estado local
  // só após sucesso — sem estado parcial em erro.
  const applyArquivar = async (anexo: GenericFileRow, archive: boolean): Promise<void> => {
    if (!config) return;
    const updates: Record<string, unknown> = archive
      ? { arquivado_em: new Date().toISOString(), arquivado_por: pessoa?.id ?? null }
      : { arquivado_em: null, arquivado_por: null };
    try {
      const { error } = await dynDb
        .from(config.tableName)
        .update(updates)
        .eq('id', anexo.id);
      if (error) throw error;
      setAnexos(prev => prev.map(a => (a.id === anexo.id ? { ...a, ...updates } : a)));
      showSuccess({ title: archive ? 'Imagem arquivada' : 'Imagem restaurada' });
    } catch (error) {
      console.error('[GenericFile] Erro ao arquivar/restaurar:', error);
      const { title, description } = getErrorMessage(error);
      showError({ title, description });
    }
  };

  // Arquivar passa por confirmação (sai do seletor de novas propostas);
  // restaurar é imediato (ação reversível e segura, R4).
  const handleToggleArquivar = (anexo: GenericFileRow): void => {
    if (anexo.arquivado_em) {
      applyArquivar(anexo, false);
    } else {
      setPendingArchive(anexo);
      setShowArchiveConfirm(true);
    }
  };

  const confirmArchive = async (): Promise<void> => {
    if (!pendingArchive) return;
    setArchiving(true);
    try {
      await applyArquivar(pendingArchive, true);
    } finally {
      setArchiving(false);
      setShowArchiveConfirm(false);
      setPendingArchive(null);
    }
  };

  const cancelArchive = (): void => {
    setShowArchiveConfirm(false);
    setPendingArchive(null);
  };

  const handleDownload = async (anexo: GenericFileRow): Promise<void> => {
    if (!config) return;
    try {
      const storagePathRaw = anexo[config.storagePathField];
      const storagePath = typeof storagePathRaw === 'string' ? storagePathRaw : null;
      if (!storagePath) throw new Error('Arquivo sem caminho de storage');
      const { data, error } = await supabase.storage
        .from(config.bucketName)
        .download(storagePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      const origRaw = config.originalNameField ? anexo[config.originalNameField] : undefined;
      const origName = typeof origRaw === 'string' && origRaw ? origRaw : null;
      a.download = origName || storagePath.split('/').pop() || 'arquivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      showError({ title: 'Erro ao baixar', description: 'Não foi possível baixar o arquivo.' });
    }
  };

  const handlePreview = (anexo: GenericFileRow): void => {
    const index = filteredAnexos.findIndex(a => a.id === anexo.id);
    if (index >= 0) {
      setPreviewIndex(index);
      setPreviewOpen(true);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingAnexo(null);
    setEditDescricao('');
    setEditCategoriaId('');
  };

  // Catálogo de file: abre o FileEditModal (instância edit já montada) para
  // editar descrição e/ou substituir o arquivo.
  const handleEdit = (anexo: GenericFileRow): void => {
    setEditingAnexo(anexo);
    const desc = config?.descriptionField ? anexo[config.descriptionField] : undefined;
    setEditDescricao(typeof desc === 'string' ? desc : '');
    setEditCategoriaId(typeof anexo._categoriaId === 'string' ? anexo._categoriaId : '');
    setShowEditModal(true);
  };

  // Entrada do save do FileEditModal. No catálogo, a troca de arquivo passa por
  // uma confirmação que nomeia as propostas afetadas (a troca atinge a view ao
  // vivo, inclusive de propostas assinadas/aceitas); demais casos aplicam direto.
  const handleSaveEdit = async (payload: FileEditPayload): Promise<void> => {
    if (isFileCatalog && payload.newFile) {
      setPendingEditPayload(payload);
      setShowEditModal(false);
      setShowReplaceConfirm(true);
      return;
    }
    await applyEdit(payload);
  };

  const confirmReplace = async (): Promise<void> => {
    if (!pendingEditPayload) return;
    try {
      await applyEdit(pendingEditPayload);
    } finally {
      setShowReplaceConfirm(false);
      setPendingEditPayload(null);
    }
  };

  const cancelReplace = (): void => {
    setShowReplaceConfirm(false);
    setPendingEditPayload(null);
    handleCancelEdit();
  };

  const applyEdit = async ({ descricao, newFile, categoriaId: newCategoriaId, customData }: FileEditPayload): Promise<void> => {
    if (!editingAnexo) return;

    setSaving(true);

    try {
      if (!config) return;
      let updatedData: Record<string, unknown>;
      if (customData) {
        // Custom fields mode: merge todos os campos
        updatedData = {};
        Object.entries(customData).forEach(([key, value]) => {
          updatedData[key] = value || null;
        });
      } else if (config.descriptionField) {
        updatedData = { [config.descriptionField]: descricao || null };
      } else {
        updatedData = {};
      }
      let newStoragePath: string | null = null;

      // Se tem novo arquivo, fazer upload e substituir
      if (newFile) {
        // Local session, not getUser(): a refresh cooldown must not read as signed out (JOSH-89).
        const user = await getSessionUser();
        if (!user) throw new Error('Usuário não autenticado');

        // Gerar nome único para o novo arquivo. No catálogo (sem foreignKey) o
        // path é proposta_imagem/<tenantId>/<uuid>.<ext> (mesmo layout do
        // uploadFile); demais modos mantêm a pasta por entidade.
        const fileExt = newFile.name.split('.').pop();
        const isCatalog = config.mode === 'catalog' || config.catalogMode;
        let baseFileName: string;
        if (isCatalog) {
          baseFileName = `${crypto.randomUUID()}.${fileExt}`;
        } else {
          const entityKeyRaw = config.foreignKey ? editingAnexo[config.foreignKey] : undefined;
          const entityIdForPath =
            (typeof entityKeyRaw === 'string' && entityKeyRaw)
            || editingAnexo.id;
          baseFileName = `${entityIdForPath}/${crypto.randomUUID()}.${fileExt}`;
        }
        const tenantId = pessoa?.tenant_id;
        newStoragePath = config.storagePath
          ? `${config.storagePath}/${tenantId}/${baseFileName}`
          : baseFileName;

        // Upload do novo arquivo (re-wrap with correct MIME)
        const uploadFile_ = withCorrectMime(newFile);
        const effectiveType = resolveContentType(newFile);
        const { error: uploadError } = await supabase.storage
          .from(config.bucketName)
          .upload(newStoragePath, uploadFile_, { contentType: effectiveType });

        if (uploadError) throw uploadError;

        // Adicionar dados do novo arquivo ao update
        updatedData[config.storagePathField] = newStoragePath;
        if (config.originalNameField) {
          updatedData[config.originalNameField] = newFile.name;
        }
        if (config.mimeTypeField) {
          updatedData[config.mimeTypeField] = effectiveType;
        }
        if (config.sizeField) {
          updatedData[config.sizeField] = newFile.size;
        }
      }

      // Atualizar registro no banco
      const { error: updateError } = await dynDb
        .from(config.tableName)
        .update(updatedData)
        .eq('id', editingAnexo.id);

      if (updateError) {
        // Se falhou e tinha novo arquivo, remover do storage
        if (newStoragePath) {
          await supabase.storage.from(config.bucketName).remove([newStoragePath]);
        }
        throw updateError;
      }

      // Se for modo junction e tem _junctionId, atualizar categoria_id em entidade_file
      let updatedCategoriaId: string | null = (typeof editingAnexo._categoriaId === 'string' ? editingAnexo._categoriaId : null);
      const junctionIdRaw = editingAnexo._junctionId;
      const junctionId = typeof junctionIdRaw === 'string' ? junctionIdRaw : null;
      if (config.mode === 'junction' && junctionId && newCategoriaId !== undefined) {
        const { error: junctionError } = await supabase
          .from('entidade_file')
          .update({ categoria_id: newCategoriaId || null })
          .eq('id', junctionId);

        if (junctionError) {
          console.error('Erro ao atualizar categoria:', junctionError);
        } else {
          updatedCategoriaId = newCategoriaId || null;
        }
      }

      // Se substituiu arquivo, deletar o antigo do storage
      const oldPathRaw = editingAnexo[config.storagePathField];
      const oldPath = typeof oldPathRaw === 'string' ? oldPathRaw : null;
      if (newFile && oldPath) {
        await supabase.storage
          .from(config.bucketName)
          .remove([oldPath]);
      }

      // Atualizar na lista local
      setAnexos(prev => prev.map(a =>
        a.id === editingAnexo.id
          ? { ...a, ...updatedData, _categoriaId: updatedCategoriaId }
          : a
      ));

      // Limpar thumbnail cache se substituiu imagem
      if (newFile) {
        setThumbnailUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[editingAnexo.id];
          return newUrls;
        });
      }

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      const { title, description } = getErrorMessage(error);
      showError({ title, description });
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    if (!bytesPerSecond || bytesPerSecond <= 0) return '';
    if (bytesPerSecond < 1024 * 1024) return (bytesPerSecond / 1024).toFixed(0) + ' KB/s';
    return (bytesPerSecond / (1024 * 1024)).toFixed(1) + ' MB/s';
  };

  const getOriginalName = (anexo: GenericFileRow): string => {
    if (!config) return 'Arquivo';
    const orig = config.originalNameField ? anexo[config.originalNameField] : undefined;
    if (typeof orig === 'string' && orig) return orig;
    const path = anexo[config.storagePathField];
    if (typeof path === 'string') {
      const last = path.split('/').pop();
      if (last) return last;
    }
    return 'Arquivo';
  };

  const getDescription = (anexo: GenericFileRow): string | null => {
    if (!config?.descriptionField) return null;
    const d = anexo[config.descriptionField];
    return typeof d === 'string' && d ? d : null;
  };

  const getMimeType = (anexo: GenericFileRow): string | null => {
    if (!config) return null;
    const raw = config.mimeTypeField ? anexo[config.mimeTypeField] : undefined;
    if (typeof raw === 'string' && raw) return raw;
    const path = anexo[config.storagePathField];
    return getMimeTypeFromPath(typeof path === 'string' ? path : null);
  };

  const getFileSize = (anexo: GenericFileRow): number | null => {
    if (!config?.sizeField) return null;
    const s = anexo[config.sizeField];
    return typeof s === 'number' ? s : null;
  };

  // Botão de ação icon-only no cluster do card (mirror dos botões download/excluir).
  const iconBtnStyle = (radius: string): CSSProperties => ({
    width: '32px',
    height: '32px',
    borderRadius: radius,
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  });

  // Ações exclusivas do catálogo de file (proposta_imagem): Arquivar/Restaurar.
  // Renderizadas só em isFileCatalog para não vazar aos consumidores
  // junction/foreignKey; gated por canEditFinal (permissão EDIT).
  const renderCatalogActions = (anexo: GenericFileRow, radius: string): ReactNode => {
    if (!isFileCatalog || readOnly || !canEditFinal) return null;
    const isArchived = !!anexo.arquivado_em;
    return (
      <>
        <button
          onClick={() => handleEdit(anexo)}
          title="Editar"
          className="gf-pen-animated"
          style={iconBtnStyle(radius)}
        >
          <PenAnimatedIcon size={18} />
        </button>
        <button
          onClick={() => handleToggleArquivar(anexo)}
          title={isArchived ? 'Restaurar' : 'Arquivar'}
          style={iconBtnStyle(radius)}
        >
          {isArchived
            ? <ArchiveRestore size={18} color="var(--color-success, #16a34a)" />
            : <Archive size={18} color="var(--muted, #6b7280)" />}
        </button>
      </>
    );
  };

  // Botão Excluir. No catálogo de file fica desabilitado com tooltip quando a
  // imagem está em uso (refCount>0): arquivar é o caminho, excluir só sem
  // vínculo (R6). A contagem é best-effort; a FK NO ACTION bloqueia no banco e
  // o erro é tratado em confirmDelete. Consolidado p/ grid e lista.
  const renderDeleteButton = (anexo: GenericFileRow, radius: string): ReactNode => {
    if (readOnly || !canDeleteFinal) return null;
    const refCount = isFileCatalog ? (refCounts[anexo.id] ?? 0) : 0;
    if (refCount > 0) {
      return (
        <button
          disabled
          title={`Em uso em ${refCount} ${refCount === 1 ? 'proposta' : 'propostas'} — arquive em vez de excluir`}
          style={{ ...iconBtnStyle(radius), cursor: 'not-allowed', opacity: 0.4 }}
        >
          <TrashAnimatedIcon size={18} />
        </button>
      );
    }
    return (
      <button
        onClick={() => handleDelete(anexo)}
        title="Excluir"
        className="gf-trash-animated"
        style={iconBtnStyle(radius)}
      >
        <TrashAnimatedIcon size={18} />
      </button>
    );
  };

  return (
    <div
      className="generic-anexos"
      style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
    >
      {/* Anexos - Grid ou Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
          Carregando anexos...
        </div>
      ) : filteredAnexos.length > 0 && layout === 'grid' ? (
        /* ===== LAYOUT GRID ===== */
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gap: '12px',
          marginBottom: '16px'
        }}>
          {filteredAnexos.map((anexo) => {
            const mimeType = getMimeType(anexo);
            const isImage = mimeType?.startsWith('image/');
            const isPdf = mimeType === 'application/pdf';

            return (
              <div
                key={anexo.id}
                style={{
                  position: 'relative',
                  background: 'white',
                  border: '1px solid var(--panel-border)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  cursor: 'pointer'
                }}
                className="anexo-card"
                onClick={() => handlePreview(anexo)}
              >
                {/* Container da Thumbnail com overlay de ações no hover */}
                <div
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    background: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                  className="anexo-thumb-container"
                >
                  {/* Badge de Arquivada (catálogo de file) */}
                  {isFileCatalog && anexo.arquivado_em ? (
                    <span style={{
                      position: 'absolute',
                      top: '6px',
                      left: '6px',
                      zIndex: 1,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'var(--muted, #6b7280)',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}>
                      Arquivada
                    </span>
                  ) : null}

                  {/* Thumbnail ou Ícone */}
                  {isImage && thumbnailUrls[anexo.id] ? (
                    <img
                      src={thumbnailUrls[anexo.id]}
                      alt={getOriginalName(anexo)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      color: isPdf ? 'var(--danger)' : isImage ? '#8b5cf6' : 'var(--muted)'
                    }}>
                      {isPdf ? (
                        <FileText size={36} />
                      ) : isImage ? (
                        <Image size={36} />
                      ) : (
                        <FileIcon size={36} />
                      )}
                      <span style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, opacity: 0.6 }}>
                        {getOriginalName(anexo).split('.').pop()}
                      </span>
                    </div>
                  )}

                  {/* Overlay de ações (aparece no hover) */}
                  <div
                    className="anexo-actions-overlay"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: 0,
                      transition: 'opacity 0.2s ease'
                    }}
                  >
                    <button
                      onClick={() => handleDownload(anexo)}
                      title="Baixar"
                      className="gf-dl-animated"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: 'transparent',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <defs>
                          <linearGradient id="gfDlD" gradientUnits="userSpaceOnUse" x1="3" y1="3" x2="21" y2="21">
                            <stop offset="0%" stopColor="#86efac" />
                            <stop offset="100%" stopColor="#4ade80" />
                          </linearGradient>
                        </defs>
                        <g className="dl-base" stroke="url(#gfDlD)">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        </g>
                        <g className="dl-arrow" stroke="url(#gfDlD)">
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" x2="12" y1="15" y2="3"/>
                        </g>
                      </svg>
                    </button>
                    {renderCatalogActions(anexo, '50%')}
                    {renderDeleteButton(anexo, '50%')}
                  </div>
                </div>

                {/* Área de Descrição (abaixo da thumbnail) */}
                <div style={{ padding: '8px 10px' }}>
                  {/* Nome do arquivo (truncado). No catálogo de file é um UUID
                      interno, então é omitido — a imagem é identificada pela
                      descrição. */}
                  {!isFileCatalog && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: 'var(--muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={getOriginalName(anexo)}
                    >
                      {getOriginalName(anexo)}
                    </div>
                  )}

                  {/* Descrição. No catálogo é o rótulo principal (substitui o
                      filename oculto), com fallback "Sem descrição" em paridade
                      com a lista — evita caption em branco. */}
                  {(isFileCatalog || getDescription(anexo)) && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: getDescription(anexo) ? '#374151' : 'var(--muted)',
                        marginTop: isFileCatalog ? 0 : '4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={getDescription(anexo) || (isFileCatalog ? 'Sem descrição' : undefined)}
                    >
                      {getDescription(anexo) || (isFileCatalog ? 'Sem descrição' : '')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : filteredAnexos.length > 0 && layout === 'list' ? (
        /* ===== LAYOUT LISTA ===== */
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: '16px'
        }}>
          {filteredAnexos.map((anexo) => {
            const mimeType = getMimeType(anexo);
            const isImage = mimeType?.startsWith('image/');
            const isPdf = mimeType === 'application/pdf';
            const fileExt = getOriginalName(anexo).split('.').pop()?.toUpperCase() || 'FILE';

            // Custom list display
            const badge = config.listBadge ? config.listBadge(anexo) : null;
            const primaryText = config.listPrimary ? config.listPrimary(anexo) : null;
            const secondaryText = config.listSecondary !== undefined ? (config.listSecondary ? config.listSecondary(anexo) : null) : undefined;
            const hasCustomDisplay = badge || primaryText !== null || secondaryText !== undefined;

            return (
              <div
                key={anexo.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: '#f9fafb',
                  border: '1px solid var(--panel-border)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  transition: 'box-shadow 0.2s ease',
                  cursor: 'pointer'
                }}
                className="anexo-card anexo-list-item"
                onClick={() => handlePreview(anexo)}
              >
                {/* Thumbnail/Ícone */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    background: 'white',
                    border: '1px solid var(--panel-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0
                  }}
                >
                  {isImage && thumbnailUrls[anexo.id] ? (
                    <img
                      src={thumbnailUrls[anexo.id]}
                      alt={getOriginalName(anexo)}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      color: isPdf ? 'var(--danger)' : isImage ? '#8b5cf6' : 'var(--muted)'
                    }}>
                      {isPdf ? <FileText size={18} /> : isImage ? <Image size={18} /> : <FileIcon size={18} />}
                    </div>
                  )}
                </div>

                {/* Informações do arquivo — 2 linhas */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {/* Linha 1: Nome do arquivo */}
                  <div style={{
                    fontSize: '13px', fontWeight: 500, color: '#111827',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }} title={hasCustomDisplay ? (secondaryText || getOriginalName(anexo)) : (isFileCatalog ? (getDescription(anexo) || 'Sem descrição') : getOriginalName(anexo))}>
                    {hasCustomDisplay ? (secondaryText || getOriginalName(anexo)) : (isFileCatalog ? (getDescription(anexo) || 'Sem descrição') : getOriginalName(anexo))}
                  </div>

                  {/* Linha 2: Descrição (se houver) + badge tipo + tamanho */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {isFileCatalog && anexo.arquivado_em ? (
                      <span style={{
                        padding: '1px 6px',
                        borderRadius: '4px',
                        background: 'var(--muted, #6b7280)',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        flexShrink: 0
                      }}>
                        Arquivada
                      </span>
                    ) : null}
                    {hasCustomDisplay ? (
                      <>
                        {badge && (
                          <span style={{
                            padding: '1px 6px', borderRadius: '4px', fontSize: '10px',
                            fontWeight: 700, background: badge.bg, color: badge.color,
                            whiteSpace: 'nowrap', flexShrink: 0
                          }}>
                            {badge.text}
                          </span>
                        )}
                        {primaryText && (
                          <span style={{
                            fontSize: '12px', color: 'var(--muted)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }} title={primaryText}>
                            {primaryText}
                          </span>
                        )}
                      </>
                    ) : isFileCatalog ? null : (
                      <>
                        {getDescription(anexo) && (
                          <span style={{
                            fontSize: '12px', color: '#374151',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }} title={getDescription(anexo) || undefined}>
                            {getDescription(anexo)}
                          </span>
                        )}
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '1px 6px',
                          background: isPdf ? '#fecaca' : isImage ? '#ddd6fe' : '#e5e7eb',
                          color: isPdf ? '#dc2626' : isImage ? '#7c3aed' : '#6b7280',
                          borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                          textTransform: 'uppercase', flexShrink: 0
                        }}>
                          {fileExt}
                        </span>
                        {getFileSize(anexo) ? (
                          <span style={{ fontSize: '12px', color: 'var(--muted)', flexShrink: 0 }}>
                            {formatFileSize(getFileSize(anexo))}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                {/* Botões de ação (alinhados à direita) */}
                <div onClick={(e) => e.stopPropagation()} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0
                }}>
                  <button
                    onClick={() => handleDownload(anexo)}
                    title="Baixar"
                    className="gf-dl-animated"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      background: 'transparent',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <defs>
                        <linearGradient id="gfDlL" gradientUnits="userSpaceOnUse" x1="3" y1="3" x2="21" y2="21">
                          <stop offset="0%" stopColor="#4ade80" />
                          <stop offset="100%" stopColor="#166534" />
                        </linearGradient>
                      </defs>
                      <g className="dl-base" stroke="url(#gfDlL)">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      </g>
                      <g className="dl-arrow" stroke="url(#gfDlL)">
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" x2="12" y1="15" y2="3"/>
                      </g>
                    </svg>
                  </button>
                  {renderCatalogActions(anexo, '6px')}
                  {renderDeleteButton(anexo, '6px')}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Estado vazio do catálogo de file (não deixar área em branco) */}
      {!loading && isFileCatalog && filteredAnexos.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--muted)', fontSize: '13px' }}>
          {searchTerm
            ? 'Nenhuma imagem encontrada'
            : showArchived
              ? 'Nenhuma imagem arquivada'
              : 'Nenhuma imagem cadastrada'}
        </div>
      )}

      {/* Área de Upload - Padrão visual do Design System */}
      {!readOnly && canInsert && (
        <div
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `1px dashed ${dragOver ? 'var(--color-info)' : '#d1d5db'}`,
            borderRadius: '8px',
            padding: '16px 20px',
            cursor: 'pointer',
            background: dragOver ? 'var(--color-primary-light)' : '#f9fafb',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {uploading ? (
            <div style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '18px',
                height: '18px',
                border: '2px solid #e5e7eb',
                borderTop: '2px solid #6366f1',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Enviando...
            </div>
          ) : (
            <>
              <Upload size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: '#374151', fontWeight: '500', fontSize: '13px' }}>
                  Arraste arquivos ou clique para selecionar
                </span>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  PDF, Imagens, Office, CSV, DWG (máx. {Math.round((config.maxFileSize || MAX_FILE_SIZE) / (1024 * 1024))}MB)
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Unified File Preview Modal */}
      <FilePreviewModal
        isOpen={previewOpen}
        files={filteredAnexos.map(a => ({
          id: a.id,
          name: getOriginalName(a),
          displayName: isFileCatalog ? (getDescription(a) || 'Sem descrição') : undefined,
          mimeType: getMimeType(a),
          size: getFileSize(a),
          storagePath: a[config.storagePathField],
          bucketName: config.bucketName
        }))}
        currentIndex={previewIndex}
        onNavigate={setPreviewIndex}
        onClose={closePreview}
        onDownload={(file: { id: string }) => {
          const anexo = filteredAnexos.find(a => a.id === file.id);
          if (anexo) handleDownload(anexo);
        }}
      />

      {/* Modal de Adicionar Arquivo (usando FileEditModal em modo add) */}
      <FileEditModal
        isOpen={showAddModal}
        onClose={cancelAdd}
        onSave={handleAddSave}
        mode="add"
        pendingFiles={pendingAddFiles}
        loading={addingFile}
        allowedExtensions={ALLOWED_EXTENSIONS}
        maxFileSize={config.maxFileSize || MAX_FILE_SIZE}
        showCategoria={config.hasObraId && categorias.length > 0}
        categoriaRequired={config.hasObraId}
        categorias={categorias}
        customFields={resolvedCustomFields}
      />

      {/* Modal de Edição de Arquivo */}
      <FileEditModal
        isOpen={showEditModal && !!editingAnexo}
        onClose={handleCancelEdit}
        onSave={handleSaveEdit}
        showFileName={!isFileCatalog}
        thumbnailUrl={editingAnexo ? thumbnailUrls[editingAnexo.id] : null}
        fileName={editingAnexo ? getOriginalName(editingAnexo) : ''}
        fileSize={editingAnexo ? getFileSize(editingAnexo) : null}
        mimeType={editingAnexo ? getMimeType(editingAnexo) : null}
        descricao={editDescricao}
        categoriaId={editCategoriaId}
        loading={saving}
        allowedExtensions={ALLOWED_EXTENSIONS}
        maxFileSize={config.maxFileSize || MAX_FILE_SIZE}
        showCategoria={config.hasObraId && categorias.length > 0}
        categoriaRequired={config.hasObraId}
        categorias={categorias}
        customFields={resolvedCustomFields}
        customFieldValues={editCustomFieldValues}
      />

      {/* Confirmação de troca de arquivo (catálogo): nomeia propostas afetadas */}
      {isFileCatalog && editingAnexo && (
        <ConfirmActionModal
          isOpen={showReplaceConfirm}
          onClose={cancelReplace}
          onConfirm={confirmReplace}
          title="Substituir arquivo da imagem"
          subtitle={(() => {
            const n = refCounts[editingAnexo.id] ?? 0;
            return n > 0
              ? `Em uso em ${n} ${n === 1 ? 'proposta' : 'propostas'}`
              : 'Imagem sem propostas vinculadas';
          })()}
          confirmText="Substituir"
          variant="primary"
          loading={saving}
        >
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>
            A nova imagem passará a ser exibida em todas as propostas que usam esta imagem,
            inclusive na visualização ao vivo de propostas já assinadas ou aceitas. PDFs já
            gerados permanecem inalterados.
          </p>
        </ConfirmActionModal>
      )}

      {/* Confirmação de arquivamento (catálogo): some do seletor, segue nas propostas */}
      {isFileCatalog && pendingArchive && (
        <ConfirmActionModal
          isOpen={showArchiveConfirm}
          onClose={cancelArchive}
          onConfirm={confirmArchive}
          title="Arquivar imagem"
          subtitle={(() => {
            const n = refCounts[pendingArchive.id] ?? 0;
            return n > 0
              ? `Em uso em ${n} ${n === 1 ? 'proposta' : 'propostas'}`
              : 'Sem propostas vinculadas';
          })()}
          confirmText="Arquivar"
          variant="primary"
          loading={archiving}
        >
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>
            A imagem sairá do seletor de novas propostas. As propostas que já a utilizam
            continuam exibindo-a normalmente, e você pode restaurá-la quando quiser.
          </p>
        </ConfirmActionModal>
      )}

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal && !!anexoToDelete}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={deleteModalTitle}
        loading={deleting}
      >
        {anexoToDelete && (
          <FileDeleteContent
            fileName={getOriginalName(anexoToDelete)}
            displayName={isFileCatalog ? (getDescription(anexoToDelete) || 'Sem descrição') : undefined}
            fileSize={getFileSize(anexoToDelete) ? formatFileSize(getFileSize(anexoToDelete)) : null}
            fileType={getMimeType(anexoToDelete)}
          />
        )}
      </ConfirmDeleteModal>

    </div>
  );
});

export default GenericFile;
